import React, { type ReactElement, useEffect, useState } from 'react';

import { type MultiLevelMenuItem } from '@/common/types/multi-level-menu.type';
import { Checkbox, List, Typography } from 'antd';
import classNames from 'classnames';

import Button from '@/common/components/button/button.component';
import CardDivider from '@/common/components/card-divider';
import Radio from '@/common/components/radio';
import TextComponent from '@/common/components/text/text.component';

import ArrowLeft from '~/public/assets/images/svg/arrow-left-gray.svg';
import ArrowRight from '~/public/assets/images/svg/arrow-right.svg';

export interface MultiLevelMenuProps {
  items: MultiLevelMenuItem[];
  title: string;
  defaultChecked?: {
    category?: MultiLevelMenuItem[] | [];
    orderBy: MultiLevelMenuItem;
  };
  menuLevel: number;
  setMenuLevel: React.Dispatch<React.SetStateAction<any>>;
  onClick?: (item: MultiLevelMenuItem | MultiLevelMenuItem[]) => void;
  onSelect?: (item: MultiLevelMenuItem | MultiLevelMenuItem[]) => void;
  isDesktop?: boolean;
}

const { Title, Text } = Typography;

// eslint-disable-next-line max-lines-per-function
export default function FilterMultiLevelMenu({
  items,
  onClick,
  title,
  onSelect,
  defaultChecked,
  menuLevel,
  setMenuLevel,
  isDesktop = false,
}: MultiLevelMenuProps): ReactElement {
  const [selected, setSelected] = useState<Omit<
    Omit<MultiLevelMenuItem, 'inputType'>,
    'children'
  > | null>(defaultChecked?.orderBy ?? null);

  const [selectedItems, setSelectedItems] = useState<
    Omit<MultiLevelMenuItem[], 'children'> | []
  >(defaultChecked?.category || []);

  const [stack, setStack] = useState([items]);
  const [titleStack, setTitleStack] = useState([title]);

  const onItemClickHandler = (item: MultiLevelMenuItem): void => {
    const { children } = item;
    if (children && children.length > 0) {
      const currentStack = stack;
      currentStack.push(children);

      const titles = titleStack;
      titles.push(item.label);

      setStack(currentStack);
      setMenuLevel((cur: number) => cur + 1);
      setTitleStack(titles);
    }

    if (onClick) {
      onClick(item);
    }
  };
  useEffect(() => {
    if (defaultChecked) setSelected(defaultChecked.orderBy);
    if (defaultChecked?.category) setSelectedItems(defaultChecked?.category);
  }, [defaultChecked]);

  const onSelectHandler = (item: MultiLevelMenuItem): void => {
    const { label, value } = item;
    setSelected({ label, value });
    if (onSelect) onSelect(item);
    if (onClick) onClick(item);
  };
  const onCheckHandler = (item: MultiLevelMenuItem, checked: boolean): void => {
    const { label, value } = item;
    if (checked) {
      setSelectedItems((prevState) => [...prevState, { label, value }]);
    } else {
      setSelectedItems((prevState) => {
        return prevState.filter((i) => i.value !== value);
      });
    }
  };

  useEffect(() => {
    if (onSelect) onSelect(selectedItems);
    if (onClick) onClick(selectedItems);
  }, [selectedItems]);

  const onBackClickHandler = (): void => {
    const currentStack = stack;
    currentStack.pop();

    const titles = titleStack;
    titles.pop();

    setTitleStack(titles);
    setMenuLevel((cur: number) => cur - 1);
    setStack(currentStack);
  };

  const renderItem = (item: MultiLevelMenuItem): ReactElement => {
    const labels = defaultChecked?.category?.map((item) => item.label);
    const labelString = labels?.join(', ');
    if (item.children && item.children.length > 0) {
      return (
        <List.Item
          onClick={() => {
            if (!isDesktop) onItemClickHandler(item);
          }}
          className=" flex items-center justify-between !border-none !px-0 !pb-[12px] !pt-[6px] first:lg:!mt-[18px]"
        >
          <Text className="text-black-default">{item.label}</Text>
          <div className="flex items-center gap-3">
            {item.value === 'ORDERBY' && defaultChecked?.orderBy && (
              <TextComponent className="text-secondary-300 lg:hidden">
                {defaultChecked?.orderBy.label}
              </TextComponent>
            )}
            {item.value === 'CATEGORY' && labelString && (
              <TextComponent className="max-w-44 truncate text-secondary-300 lg:hidden">
                {labelString}
              </TextComponent>
            )}
            <ArrowLeft className="text-secondary-300 lg:hidden" />
          </div>
        </List.Item>
      );
    }
    return item.inputType === 'radio' ? (
      <List.Item
        onClick={() => {
          onSelectHandler(item);
        }}
        className=" flex items-center justify-between !border-none !px-0 !pb-[12px] !pt-[6px] first:!pt-[12px] lg:text-[#474239]"
      >
        <Text>{item.label}</Text>
        <Radio
          className="radio ml-0"
          value={item.value}
          checked={item.value === selected?.value}
        />
      </List.Item>
    ) : item.inputType === 'title' ? (
      <TextComponent className="text-sm font-medium text-[#191308]">
        {item.label}
      </TextComponent>
    ) : (
      <List.Item className="max-lg:!px-4 flex items-center justify-between !border-none !pb-[12px] !pt-[6px] first:!mt-[18px] lg:!px-0">
        <label className="flex w-full justify-between">
          <Text>{item.label}</Text>
          <Checkbox
            value={item.value}
            onChange={(e) => {
              onCheckHandler(item, e.target.checked);
            }}
            checked={defaultChecked?.category?.some(
              (checkedItem) => checkedItem.value === item.value
            )}
          />
        </label>
      </List.Item>
    );
  };

  return (
    <div>
      <div className="z-10 m-auto flex w-full flex-col items-center justify-start gap-[11.5px] rounded-t-2xl  bg-white !px-0 !py-3 lg:!h-full lg:!justify-between">
        <div className="flex w-full grow flex-row items-center lg:items-start">
          {menuLevel > 0 && (
            <div onClick={onBackClickHandler}>
              <ArrowRight className="mx-2 text-secondary-300" />
            </div>
          )}
          <Title
            level={5}
            {...(menuLevel > 0 && { onClick: onBackClickHandler })}
            className={classNames(
              '!my-0 !text-sm font-medium !text-[#748DA9] lg:!font-semibold lg:!text-[#174170]',
              {
                'px-1': !menuLevel,
              }
            )}
          >
            {titleStack[menuLevel]}
          </Title>
        </div>
        <CardDivider type="item" itemClassName="!px-0 " />
        <List
          size="small"
          dataSource={stack[menuLevel]}
          renderItem={(item) => renderItem(item)}
          className={`w-full  grow lg:!pt-0`}
        />
        <CardDivider type="item" itemClassName="!px-0 hidden lg:!flex" />
        {menuLevel > 0 && (
          <Button
            type="black"
            className="w-full lg:m-2 lg:hidden"
            onClick={onBackClickHandler}
          >
            ثبت
          </Button>
        )}
      </div>
    </div>
  );
}
